import {
  generateArraySchema,
  generateColorSchema,
  generateNumberSchema,
  generateObjectSchema,
  generateRootObjectSchema,
  generateStringSchema,
} from "components/cards/common/schema";
import { BaseAtomSpec, generateAtomContainerSchema } from "components/cards/atoms/BaseAtomSpec";
import { TextParticleSpec, type TextParticleProps } from "components/cards/particles/YinzCam";

const manhattanTextDefaults = {
  selectedInning: {
    size: "1rem",
    weight: "700",
    color: "var(--theme-mode-background-color, #FFFFFF)",
    align: "center",
  },
  inning: {
    size: "1rem",
    weight: "700",
    color: "var(--theme-mode-foreground-color, #777777)",
    align: "center",
  },
  legend: {
    size: "0.75rem",
    weight: "500",
    color: "var(--theme-mode-foreground-color, #000020)",
  },
  totalStatLabel: {
    size: '0.75rem',
    weight: '500',
    color: 'var(--theme-mode-foreground-color, #000020)',
    text: 'Total runs',
  },
  boundaryStatLabel: {
    size: '0.75rem',
    weight: '500',
    color: 'var(--theme-mode-background-color, #fff)',
    text: 'From boundaries',
  },
  bestOverStatLabel: {
    size: '0.75rem',
    weight: '500',
    color: 'color-mix(in srgb, var(--theme-mode-foreground-color, #000020) 70%, transparent)',
    text: 'Best over',
  },
  statValue: {
    size: "1.125rem",
    weight: "700",
  },
  totalStatValue: {
    color: 'var(--theme-mode-foreground-color, #000020)',
  },
  boundaryStatValue: {
    color: 'var(--theme-mode-background-color, #fff)',
  },
  bestOverStatValue: {
    color: 'color-mix(in srgb, var(--theme-mode-foreground-color, #000020) 70%, transparent)',
  },
  tooltip: {
    size: "0.625rem",
    weight: "400",
    color: "var(--theme-mode-foreground-color, #000020)",
  },
  empty: {
    size: "0.75rem",
    weight: "500",
    color: "var(--theme-mode-foreground-color, #000020)",
    align: "center",
  },
} as unknown as Record<string, TextParticleProps>;

const manhattanColorDefaults = {
  boundaryColor: "var(--theme-mode-highlight-color, #004899)",
  otherRunsColor: "var(--theme-mode-accent-color, #1AFFBA)",
} as const;

const overSchema = generateObjectSchema(
  "Over",
  "A single over in an innings.",
  {
    over: generateNumberSchema("Over", "The over number."),
    totalRuns: generateNumberSchema("Total Runs", "Total runs scored in this over."),
    boundaryRuns: generateNumberSchema("Boundary Runs", "Runs scored from fours and sixes in this over."),
    otherRuns: generateNumberSchema("Other Runs", "Runs scored from non-boundaries in this over."),
    wickets: generateNumberSchema("Wickets", "Wickets lost in this over."),
    fourRuns: generateNumberSchema("Four Runs", "Runs from fours in this over."),
    sixRuns: generateNumberSchema("Six Runs", "Runs from sixes in this over."),
    fourCount: generateNumberSchema("Four Count", "The number of fours in this over."),
    sixCount: generateNumberSchema("Six Count", "The number of sixes in this over."),
  } as any,
  { additionalProperties: true }
);

const inningSchema = generateObjectSchema(
  "Inning",
  "A batting innings for the Manhattan chart.",
  {
    number: generateNumberSchema("Innings Number", "The source innings number."),
    battingInning: generateNumberSchema("Batting Inning", "The team batting occurrence, usually 1 or 2."),
    teamId: generateStringSchema("Team ID", "The team identifier."),
    teamName: generateStringSchema("Team Name", "The team name."),
    teamTriCode: generateStringSchema("Team Tricode", "The team abbreviation."),
    label: generateStringSchema("Tab Label", "The label to display in the innings tab."),
    score: generateStringSchema("Score", "The innings score."),
    oversDisplay: generateStringSchema("Overs Display", "The formatted overs display."),
    overs: generateArraySchema("Overs", "The overs in this innings.", overSchema),
  } as any,
  { additionalProperties: true }
);

export const specification = new BaseAtomSpec(
  "Manhattan Chart Card",
  generateRootObjectSchema({
    container: generateAtomContainerSchema({
      defaultValues: { fillWidth: true, themeMode: "primary" },
    }),
    selectedInningIndex: generateNumberSchema("Selected Inning Index", "The initially selected innings tab.", {
      defaultValue: 0,
    }),
    chartHeight: generateStringSchema("Chart Height", "The visual height of the chart in standard CSS units.", {
      defaultValue: "14.9375rem",
    }),
    emptyMessage: generateStringSchema("Empty Message", "Text shown before an innings has batting data.", {
      defaultValue: "Graph will display once the team has begun batting.",
    }),
    boundaryLegendText: generateStringSchema("Boundary Legend Text", "Legend label for boundary runs.", {
      defaultValue: "Boundaries",
    }),
    otherRunsLegendText: generateStringSchema("Other Runs Legend Text", "Legend label for non-boundary runs.", {
      defaultValue: "Other Runs",
    }),
    innings: generateArraySchema("Innings", "The normalized Manhattan chart innings.", inningSchema),
    txtSelectedInning: TextParticleSpec.generateSchema("Selected Inning Label", "Selected innings tab text.", {
      defaultValues: manhattanTextDefaults.selectedInning,
      uneditableFields: { text: true },
    }),
    txtInning: TextParticleSpec.generateSchema("Inning Label", "Unselected innings tab text.", {
      defaultValues: manhattanTextDefaults.inning,
      uneditableFields: { text: true },
    }),
    txtLegend: TextParticleSpec.generateSchema("Legend Label", "Legend text.", {
      defaultValues: manhattanTextDefaults.legend,
      uneditableFields: { text: true },
    }),
    txtTotalStatLabel: TextParticleSpec.generateSchema(
      'Total Runs Label',
      'Total runs summary statistic label text.',
      {
        defaultValues: manhattanTextDefaults.totalStatLabel,
      }
    ),
    txtBoundaryStatLabel: TextParticleSpec.generateSchema(
      'Boundaries Label',
      'From boundaries summary statistic label text.',
      {
        defaultValues: manhattanTextDefaults.boundaryStatLabel,
      }
    ),
    txtBestOverStatLabel: TextParticleSpec.generateSchema(
      'Best Over Label',
      'Best over summary statistic label text.',
      {
        defaultValues: manhattanTextDefaults.bestOverStatLabel,
      }
    ),
    txtStatValue: TextParticleSpec.generateSchema("Stat Value", "Summary statistic value text.", {
      defaultValues: manhattanTextDefaults.statValue,
      uneditableFields: { text: true },
    }),
    txtTotalStatValue: TextParticleSpec.generateSchema('Total Runs Value', 'Total runs summary statistic value text.', {
      defaultValues: manhattanTextDefaults.totalStatValue,
      uneditableFields: { text: true },
    }),
    txtBoundaryStatValue: TextParticleSpec.generateSchema(
      'Boundaries Value',
      'From boundaries summary statistic value text.',
      {
        defaultValues: manhattanTextDefaults.boundaryStatValue,
        uneditableFields: { text: true },
      }
    ),
    txtBestOverStatValue: TextParticleSpec.generateSchema('Best Over Value', 'Best over summary statistic value text.', {
      defaultValues: manhattanTextDefaults.bestOverStatValue,
      uneditableFields: { text: true },
    }),
    txtTooltip: TextParticleSpec.generateSchema("Tooltip Text", "Tooltip text.", {
      defaultValues: manhattanTextDefaults.tooltip,
      uneditableFields: { text: true },
    }),
    txtEmpty: TextParticleSpec.generateSchema("Empty State Text", "Empty chart message text.", {
      defaultValues: manhattanTextDefaults.empty,
      uneditableFields: { text: true },
    }),
    boundaryColor: generateColorSchema("Boundary Bar Color", "Runs from boundaries.", {
      defaultValue: manhattanColorDefaults.boundaryColor,
    }),
    otherRunsColor: generateColorSchema("Other Runs Bar Color", "Runs from non-boundaries.", {
      defaultValue: manhattanColorDefaults.otherRunsColor,
    }),
    tabSelectedColor: generateColorSchema('Selected Tab Color', 'Background of the active innings tab.', {
      defaultValue: 'var(--theme-mode-highlight-color, #004899)',
    }),
    statTotalBackground: generateColorSchema('Total Runs Card Background', 'Background of the total runs summary card.', {
      defaultValue: 'var(--theme-mode-accent-color, #1affba)',
    }),
    statBoundariesBackground: generateColorSchema('Boundaries Card Background', 'Background of the from-boundaries summary card.', {
      defaultValue: 'var(--theme-mode-highlight-color, #004899)',
    }),
    statBestOverBackground: generateColorSchema('Best Over Card Background', 'Background of the best over summary card.', {
      defaultValue: 'color-mix(in srgb, var(--theme-mode-foreground-color, #000020) 11%, transparent)',
    }),
  } as any)
);
